import { useLanguage } from '@/hooks/useLanguage';
import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { MetricDetailModal } from './MetricDetailModal';

interface MetricCardProps {
  icon: LucideIcon;
  title: string;
  value: number | string;
  /** Não é exibido no card — segue apenas como contexto para o modal de detalhe. */
  subtitle: string;
  subtitleColor?: string;
  index: number;
  isLoading?: boolean;
  formatValue?: (v: number) => string;
  sparkData?: number[];
  metricKey?: string;
}

// Mantém a mesma caixa do card carregado (altura, borda e raio) para não haver salto de layout
function SkeletonCard() {
  useLanguage();
  return (
    <div className="min-w-0 bg-white border border-[#D9E1EC] rounded-[16px] h-[102px] flex flex-col justify-center px-4 animate-pulse">
      <div className="flex items-center gap-1.5 mb-0.5">
        <div className="w-2.5 h-2.5 rounded bg-[#F3F4F6]" />
        <div className="h-2 w-12 rounded bg-[#F3F4F6]" />
      </div>
      <div className="h-3 w-10 rounded bg-[#F3F4F6]" />
    </div>
  );
}

export function MetricCard({ icon: Icon, title, value, subtitle, subtitleColor, index, isLoading, formatValue, sparkData, metricKey }: MetricCardProps) {
  useLanguage();
  const [detailOpen, setDetailOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  if (isLoading) return <SkeletonCard />;

  const displayValue = typeof value === 'string'
    ? value
    : (formatValue
        ? formatValue(typeof value === 'number' ? value : 0)
        : (typeof value === 'number' ? value.toLocaleString(getInterfaceLanguage()) : value));

  // Série toda zerada vira uma reta que não informa nada — nesse caso o card não abre o modal
  const hasSparkData = sparkData && sparkData.length >= 2 && sparkData.some((v) => v > 0);

  return (
    <>
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        className="min-w-0 bg-white border border-[#D9E1EC] rounded-[16px] px-5 py-4 h-[102px] flex items-center gap-4 shadow-[0_1px_2px_rgba(16,24,40,.04)] cursor-pointer select-none group transition-all"
        onClick={hasSparkData ? () => {
          if (cardRef.current) setAnchorRect(cardRef.current.getBoundingClientRect());
          setDetailOpen(true);
        } : undefined}
      >
        <div className="flex-shrink-0 flex items-center justify-center w-[52px] h-[52px] rounded-[14px] bg-[#F0F5FF]">
          <Icon className="w-[26px] h-[26px] text-[#3478F6]" strokeWidth={2} />
        </div>

        <div className="flex flex-col min-w-0">
          <span className="text-[13px] font-medium text-[#64748B] truncate normal-case tracking-normal">
            {title}
          </span>
          <span className="text-[#0F172A] text-[26px] min-[1300px]:text-[28px] font-bold tabular-nums tracking-tight leading-none mt-1.5">
            {displayValue}
          </span>
        </div>
      </motion.div>

      {hasSparkData && (
        <MetricDetailModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          icon={Icon}
          title={title}
          value={displayValue}
          subtitle={subtitle}
          sparkData={sparkData}
          anchorRect={anchorRect}
          metricKey={metricKey || 'plays'}
        />
      )}
    </>
  );
}
